import copyToClipboard from 'copy-to-clipboard';
import React, {type FC, useContext, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {Transforms} from 'slate';
import {type RenderElementProps, useSlateStatic} from 'slate-react';

import {useSelector} from '../../../../state/hooks';
import {
  addNormalizedPanelGrid,
  removeNormalizedPanelGrid,
} from '../../../../state/views/actionsInternal';
import {normalize} from '../../../../state/views/normalize';
import type * as SectionTypes from '../../../../state/views/section/types';
import {WBSlateReduxBridgeContext} from '../../WBSlateReduxBridgeContext';
import {BlockWrapper} from '../drag-drop/BlockWrapper';
import {PanelGrid} from './PanelGrid';
import * as S from './PanelGridElement.styles';
import {type PanelGrid as PanelGridType} from './types';

/**
 * This is an insane, indecipherable state machine, and I will
 * forget how it works in a couple weeks. So I hope it doesn't break.
 * The complexity comes from the fact that panel grids (ReportPanelGridSection.tsx)
 * expect refs and make their updates through our normalized Redux architecture.
 * So we need to make a normalized copy of the panel grid's metadata.
 * We then need to mirror changes made to the normalized copy
 * onto the denormalized copy. Simple enough. But there's a catch:
 * when the user hits undo, a change is made only to the denormalized copy.
 * We then need to renormalize to update what's actually displayed.
 * This is prone to infinite loops, which the state machine is designed to prevent.
 */
export const PanelGridElement: FC<
  RenderElementProps & {
    element: PanelGridType;
  }
> = ({attributes, element, children}) => {
  const {viewRef} = useContext(WBSlateReduxBridgeContext);
  if (viewRef == null) {
    throw new Error(`panel grid rendered with null view ref`);
  }
  const editor = useSlateStatic();

  const dispatch = useDispatch();
  const {metadata} = element;
  const updateDenormalizedOnNormalizedUpdate = useRef(true);
  const renormalizeOnDenormalizedUpdate = useRef(true);
  const partRefRef = useRef<SectionTypes.Ref | null>(null);

  /** On an undo, renormalize. */
  useEffect(() => {
    if (renormalizeOnDenormalizedUpdate.current) {
      updateDenormalizedOnNormalizedUpdate.current = false;

      if (partRefRef.current != null) {
        dispatch(removeNormalizedPanelGrid(viewRef.id, partRefRef.current));
      }

      const {partRef, partsWithRefs} = normalize(
        'section',
        viewRef.id,
        metadata
      );

      partRefRef.current = partRef;
      dispatch(addNormalizedPanelGrid(viewRef.id, partsWithRefs));
    }
    renormalizeOnDenormalizedUpdate.current = true;
  }, [dispatch, metadata, viewRef.id]);

  /** On dismount, remove from redux. */
  useEffect(() => {
    return () => {
      dispatch(removeNormalizedPanelGrid(viewRef.id, partRefRef.current));
    };
  }, [dispatch, viewRef.id]);

  const isLoadedIntoRedux = useSelector(
    state =>
      partRefRef.current != null &&
      state.views.parts.section[partRefRef.current.id] != null
  );

  if (partRefRef.current == null || !isLoadedIntoRedux) {
    return null;
  }

  return (
    <BlockWrapper attributes={attributes} element={element} disableClickSelect>
      <S.PanelGridWrapper
        onMouseUp={e => {
          // This causes the blue highlight around the panel grid dots and run sets because we want the
          // `Add panel` popup menu to be the primary focus. This mouse action happens if you click on grid
          // runset area.
          const selection = window.getSelection();
          if (selection && selection.isCollapsed) {
            // If no text is selected, deselect the editor
            Transforms.deselect(editor);
          }
        }}
        onCopy={(event: React.ClipboardEvent<HTMLDivElement>) => {
          // In order to allow copying of text in a panel grid (ex: run name, panel title), we need to override
          // Slate's default onCopy behavior and stop the event from bubbling from the SlateElement level. Slate' default
          // onCopy behavior is still used for other elements unless there is explicit override in the Slate editor.
          const selection = window.getSelection();
          if (selection && selection.rangeCount) {
            const range = selection.getRangeAt(0);
            const selectedText = range.toString();
            if (selectedText) {
              copyToClipboard(selectedText);
              event.stopPropagation();
            }
          }
        }}>
        {children}
        <PanelGrid
          updateDenormalizedOnNormalizedUpdate={
            updateDenormalizedOnNormalizedUpdate
          }
          renormalizeOnDenormalizedUpdate={renormalizeOnDenormalizedUpdate}
          element={element}
          partRef={partRefRef.current}
        />
      </S.PanelGridWrapper>
    </BlockWrapper>
  );
};
